import { Button, Dropzone, Flex, Spacing, Text } from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { FEC_IMPORT_FILE_ACCEPT, MAX_FEC_FILE_SIZE_BYTES, MAX_FEC_IMPORT_FILES } from '../../constants/fecImportUpload'
import { useFecImportSessionContext } from '../../contexts/fecImportSessionContext'
import { FecImportFileError } from '../../enums/fecImportFileError'
import { useFecImportUpload } from '../../hooks/useFecImportUpload'
import { useFetchFecImports } from '../../hooks/useFetchFecImports'
import { getFecImportDroppedFiles } from '../../utils/getFecImportDroppedFiles'
import { getFecImportFileError } from '../../utils/getFecImportFileError'
import { isFecImportConflictError } from '../../utils/isFecImportConflictError'
import { FecImportFileListSkeleton } from '../FecImportFileListSkeleton'
import { FecImportFileList, FecImportFileListItem } from './elements/FecImportFileList'
import * as Styled from './styles'

export const FecImportUploadStep = (): ReactElement => {
  const { onImportCreated, onUploadComplete, sessionFiles } = useFecImportSessionContext()
  const [fileErrors, setFileErrors] = useState<Map<File, FecImportFileError>>(() => new Map())
  const [pendingFiles, setPendingFiles] = useState<File[]>(sessionFiles)
  const isSubmittingRef = useRef(false)

  const { t } = useTranslation()
  const { isLoading: isLoadingFecImports } = useFetchFecImports()
  const { fileProgress, isUploading, startUpload } = useFecImportUpload()

  const remainingSlots = Math.max(MAX_FEC_IMPORT_FILES - pendingFiles.length, 0)
  const hasPendingFiles = pendingFiles.length > 0
  const hasInvalidPendingFiles = pendingFiles.some((file) => !!getFecImportFileError(file))
  const isUploadActionable = hasPendingFiles && !hasInvalidPendingFiles

  const handleRemovePendingFile = useCallback((index: number) => {
    setPendingFiles((previousPendingFiles) => {
      const removedFile = previousPendingFiles[index]

      if (removedFile) {
        setFileErrors((currentErrors) => {
          if (!currentErrors.has(removedFile)) {
            return currentErrors
          }

          const nextErrors = new Map(currentErrors)
          nextErrors.delete(removedFile)

          return nextErrors
        })
      }

      return previousPendingFiles.filter((_, fileIndex) => fileIndex !== index)
    })
  }, [])

  const fileListItems: FecImportFileListItem[] = useMemo(
    () =>
      pendingFiles.map((file, index) => ({
        error: getFecImportFileError(file) ?? fileErrors.get(file),
        fileName: file.name,
        fileSize: file.size,
        onRemove: isUploading ? undefined : () => handleRemovePendingFile(index),
        progressPercent: isUploading ? fileProgress.get(file) : undefined,
      })),
    [fileErrors, fileProgress, handleRemovePendingFile, isUploading, pendingFiles],
  )

  const handleDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    const droppedFiles = getFecImportDroppedFiles(acceptedFiles, fileRejections)

    setPendingFiles((previousPendingFiles) => {
      const remainingFileSlots = Math.max(MAX_FEC_IMPORT_FILES - previousPendingFiles.length, 0)

      return [...previousPendingFiles, ...droppedFiles.slice(0, remainingFileSlots)]
    })
  }, [])

  const handleUploadButtonClick = useCallback(async () => {
    if (hasInvalidPendingFiles || isSubmittingRef.current) {
      return
    }

    isSubmittingRef.current = true
    const uploadedFiles = new Set<File>()
    setFileErrors(new Map())

    try {
      await startUpload(pendingFiles, {
        onFileUploaded: (file) => uploadedFiles.add(file),
        onImportCreated,
      })
      if (uploadedFiles.size === 0) {
        return
      }

      setPendingFiles([])
      onUploadComplete([...uploadedFiles])
    } catch (error) {
      const failedFile = pendingFiles.find((file) => !uploadedFiles.has(file))
      setPendingFiles((currentPendingFiles) => currentPendingFiles.filter((file) => !uploadedFiles.has(file)))

      if (failedFile) {
        setFileErrors(
          new Map([
            [
              failedFile,
              isFecImportConflictError(error) ? FecImportFileError.AlreadyImported : FecImportFileError.UploadFailed,
            ],
          ]),
        )
      }
    } finally {
      isSubmittingRef.current = false
    }
  }, [hasInvalidPendingFiles, onImportCreated, onUploadComplete, pendingFiles, startUpload])

  const renderDropzoneSlot = (): ReactElement => {
    if (isLoadingFecImports) {
      return <FecImportFileListSkeleton />
    }

    return (
      <Dropzone
        accept={FEC_IMPORT_FILE_ACCEPT}
        contentText={t('settings.organization.accounting.fec_file_import.modal.upload_step.dropzone_text')}
        disabled={isUploading || remainingSlots === 0}
        maxFiles={remainingSlots}
        maxSize={MAX_FEC_FILE_SIZE_BYTES}
        onDrop={handleDrop}
      />
    )
  }

  return (
    <Styled.UploadContainer>
      <Flex flexDirection="column" gap={Spacing.S16}>
        <Flex flexDirection="column" gap={Spacing.S8}>
          <Text colorVariant="primary" variant="h4">
            {t('settings.organization.accounting.fec_file_import.modal.upload_step.title')}
          </Text>
          <Text colorVariant="secondary" variant="bodyMD">
            {t('settings.organization.accounting.fec_file_import.modal.upload_step.description')}
          </Text>
        </Flex>

        <Flex flexDirection="column" gap={Spacing.S8}>
          {renderDropzoneSlot()}

          <Text colorVariant="discreet" variant="bodySM">
            {t('settings.organization.accounting.fec_file_import.modal.upload_step.format_hint')}
          </Text>
        </Flex>

        {!isLoadingFecImports && fileListItems.length > 0 && <FecImportFileList items={fileListItems} />}

        <Flex justifyContent="flex-end">
          <Button
            disabled={isUploading || isLoadingFecImports || !isUploadActionable}
            loading={isUploading}
            onClick={handleUploadButtonClick}
            variant="filled"
          >
            {t('settings.organization.accounting.fec_file_import.modal.upload_step.upload_button')}
          </Button>
        </Flex>
      </Flex>
    </Styled.UploadContainer>
  )
}
